import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import {
  AccountBalance as TransactionsIcon,
  AccountBalanceWallet as WalletsIcon,
  TrendingUp as InvestmentsIcon,
  MonetizationOn as LoansIcon,
  CreditCard as CreditCardsIcon,
  Calculate as CalculatorIcon,
  Handyman as UtilitiesIcon,
  Shield as InsuranceIcon,
  Person as ProfileIcon,
  AccountBalance as BankIcon,
  CreditCard as CreditIcon,
  Wallet as CashIcon,
  TrendingUp as NetWorthIcon,
  TrendingUp,
  AttachMoney as MoneyIcon,
  AccountBalanceWallet as NetInhandIcon,
  ShowChart as TotalNetWorthIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { formatCurrency } from '../utils/formatters';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';
import InsuranceOverview from '../components/dashboard/InsuranceOverview';
import RecentActivity from '../components/dashboard/RecentActivity';
import { useAccess } from '../contexts/AccessContext';
import { moduleForPath } from '../config/modules';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const dashboardTour = useTour('dashboard', { ready: !loading });
  const { isEnabled } = useAccess();
  const [accountSummary, setAccountSummary] = useState({
    bankAccounts: 0,
    creditCards: 0,
    cashWallets: 0,
    netWorth: 0
  });

  const dashboardItems = [
    {
      title: 'Transactions',
      description: 'View and manage all your financial transactions',
      icon: TransactionsIcon,
      path: '/transactions',
      color: 'primary.main'
    },
    {
      title: 'Wallets',
      description: 'Manage your bank accounts and digital wallets',
      icon: WalletsIcon,
      path: '/wallets',
      color: 'success.main'
    },
    {
      title: 'Investments',
      description: 'Track your investment portfolio and performance',
      icon: InvestmentsIcon,
      path: '/investments',
      color: 'info.main'
    },
    {
      title: 'Loans',
      description: 'Monitor your loans and repayment schedules',
      icon: LoansIcon,
      path: '/loans',
      color: 'warning.main'
    },
    {
      title: 'Insurance',
      description: 'Policies, cover and premium renewals',
      icon: InsuranceIcon,
      path: '/insurance',
      color: '#16a34a'
    },
    {
      title: 'Credit Cards',
      description: 'Manage credit cards, bills, and rewards',
      icon: CreditCardsIcon,
      path: '/credit-cards',
      color: 'error.main'
    },
    {
      title: 'Finance Calculator',
      description: 'Calculate EMIs, SIPs, and other financial metrics',
      icon: CalculatorIcon,
      path: '/finance-calculator',
      color: 'secondary.main'
    },
    {
      title: 'Utilities',
      description: 'Fuel efficiency and other handy tools',
      icon: UtilitiesIcon,
      path: '/utilities',
      color: '#0891b2'
    },
    {
      title: 'Profile',
      description: 'Manage your account settings and preferences',
      icon: ProfileIcon,
      path: '/profile-update',
      color: 'text.primary'
    }
  ];

  // A card statistic that opens Credit Cards with the matching filter
  const statLinkProps = (filter, label) => ({
    role: 'link',
    tabIndex: 0,
    'aria-label': `Show ${label.toLowerCase()} credit cards`,
    onClick: (e) => {
      e.stopPropagation();
      navigate(filter === 'all' ? '/credit-cards' : `/credit-cards?filter=${filter}`);
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        navigate(filter === 'all' ? '/credit-cards' : `/credit-cards?filter=${filter}`);
      }
    },
    sx: {
      textAlign: 'center',
      py: 1,
      borderRadius: 2,
      cursor: 'pointer',
      transition: 'background-color .15s',
      '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' },
    },
  });

  const fetchAccountSummary = async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('User authentication error:', userError);
        return;
      }

      // Fetch all wallets for the user
      const { data: wallets, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_archived', false);

      if (walletsError) {
        console.error('Error fetching wallets:', walletsError);
        return;
      }

      // Calculate balances by wallet type
      let bankTotal = 0;
      let creditTotal = 0;
      let cashTotal = 0;

      wallets?.forEach(wallet => {
        const balance = wallet.balance || 0;

        switch (wallet.type) {
          case 'bank':
            bankTotal += balance;
            break;
          case 'credit':
            // For credit cards, negative balance means used amount
            // We want to show total used amount across all credit cards
            const usedAmount = balance < 0 ? Math.abs(balance) : 0;
            creditTotal += usedAmount;
            break;
          case 'cash':
          case 'digital':
            cashTotal += balance;
            break;
          default:
            break;
        }
      });

      // Calculate net worth (bank + cash - credit card debt)
      const creditDebt = wallets
        ?.filter(w => w.type === 'credit' && w.balance < 0)
        .reduce((total, w) => total + Math.abs(w.balance), 0) || 0;

      const netWorth = bankTotal + cashTotal - creditDebt;

      // Fetch investment data
      let investmentTotal = 0;

      try {
        // Fetch all investment tables and sum their values
        const investmentTables = [
          'stocks_investments',
          'equity_etf_investments',
          'mutual_fund_equity_investments',
          'nps_investments',
          'govt_bond_investments',
          'corp_bond_investments',
          'ncd_investments',
          'fixed_deposit_investments',
          'debt_mutual_fund_investments',
          'ppf_investments',
          'epf_investments',
          'nps_debt_investments',
          'debt_others_investments',
          'gold_bond_investments',
          'gold_etf_investments',
          'digital_gold_investments',
          'gold_mutual_fund_investments',
          'physical_gold_investments',
          'reit_investments',
          'liquid_etf_investments',
          'mutual_fund_investments',
          'crypto_investments',
          'other_investments'
        ];

        for (const table of investmentTables) {
          try {
            const { data, error } = await supabase
              .from(table)
              .select('amount_invested')
              .eq('user_id', user.id);

            if (!error && data) {
              investmentTotal += data.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
            }
          } catch (tableError) {
            // Continue if table doesn't exist or has errors
            console.log(`Table ${table} not accessible:`, tableError.message);
          }
        }

        // Handle special cases
        try {
          // Real estate direct (uses 'price' field)
          const { data: realEstateData, error: realEstateError } = await supabase
            .from('real_estate_direct_investments')
            .select('price')
            .eq('user_id', user.id);

          if (!realEstateError && realEstateData) {
            investmentTotal += realEstateData.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
          }
        } catch (error) {
          console.log('Real estate table not accessible');
        }

        try {
          // Recurring deposits (calculate current value)
          const { data: rdData, error: rdError } = await supabase
            .from('recurring_deposit_investments')
            .select('monthly_investment, start_date')
            .eq('user_id', user.id);

          if (!rdError && rdData) {
            const rdTotal = rdData.reduce((sum, item) => {
              const start = new Date(item.start_date);
              const today = new Date();

              const monthsDiff = (today.getFullYear() - start.getFullYear()) * 12 +
                (today.getMonth() - start.getMonth());

              if (monthsDiff < 0) return sum;

              const adjustedMonths = start.getDate() > today.getDate() ? monthsDiff : monthsDiff + 1;
              const contribution = item.monthly_investment * Math.max(0, adjustedMonths);
              return sum + contribution;
            }, 0);
            investmentTotal += rdTotal;
          }
        } catch (error) {
          console.log('Recurring deposits table not accessible');
        }

      } catch (error) {
        console.error('Error fetching investment data:', error);
      }

      // Fetch loan data - use the same calculation as Loans page
      let loanTotal = 0;

      try {
        // Fetch all loans with their details
        const { data: loansData, error: loansError } = await supabase
          .from('loans')
          .select('*')
          .eq('user_id', user.id);

        if (!loansError && loansData && loansData.length > 0) {
          // Fetch all payments for these loans
          const { data: paymentsData, error: paymentsError } = await supabase
            .from('loan_payments')
            .select('*')
            .in('loan_id', loansData.map(loan => loan.id));

          if (!paymentsError && paymentsData) {
            // Use the same calculation logic as Loans.js
            const { calculateLoanDetails } = await import('../utils/loanCalculations');

            loanTotal = loansData.reduce((sum, loan) => {
              const loanPayments = paymentsData.filter(p => p.loan_id === loan.id) || [];
              const details = calculateLoanDetails(loan, loanPayments);

              // Calculate total amount owed and remaining
              const totalAmount = loan.principal_amount + details.totalInterestAccrued;
              const remainingAmount = Math.max(0, totalAmount - details.totalPaid);

              return sum + remainingAmount;
            }, 0);
          } else {
            // Fallback to database remaining_amount if payments fetch fails
            loanTotal = loansData.reduce((sum, loan) => sum + (Number(loan.remaining_amount) || 0), 0);
          }
        } else if (loansError) {
          console.error('Error fetching loans:', loansError);
        }

      } catch (error) {
        console.error('Error fetching loan data:', error);
        // Fallback to simple sum if calculation fails
        try {
          const { data: loansData } = await supabase
            .from('loans')
            .select('remaining_amount')
            .eq('user_id', user.id);

          if (loansData) {
            loanTotal = loansData.reduce((sum, loan) => sum + (Number(loan.remaining_amount) || 0), 0);
          }
        } catch (fallbackError) {
          console.error('Fallback loan calculation also failed:', fallbackError);
        }
      }

      // Fetch credit card statistics
      let creditCardStats = {
        used: 0,
        billGenerated: 0,
        fullyPaid: 0,
        unpaid: 0,
        partiallyPaid: 0,
        totalRemainingPayment: 0
      };

      try {
        // Fetch credit cards with details and transactions
        const { data: creditCardsData, error: creditCardsError } = await supabase
          .from('wallets')
          .select(`
            id,
            name,
            balance,
            credit_limit,
            is_archived,
            credit_card_details (
              billing_date,
              due_date
            ),
            transactions (
              id,
              amount,
              date,
              type
            )
          `)
          .eq('user_id', user.id)
          .eq('type', 'credit')
          .eq('is_archived', false);

        if (!creditCardsError && creditCardsData) {
          creditCardStats.total = creditCardsData.length;

          creditCardsData.forEach(card => {
            const details = card.credit_card_details[0] || {};
            const transactions = card.transactions || [];

            // Check if card is used (has negative balance)
            const isUsed = card.balance < 0;
            if (isUsed) {
              creditCardStats.used++;
            }

            // Calculate payment status if billing dates are set
            if (details.billing_date && details.due_date) {
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonth = today.getMonth();
              const currentDay = today.getDate();
              const billingDay = parseInt(details.billing_date);
              const dueDay = parseInt(details.due_date);

              // Calculate billing cycle dates
              let currentBillDate, currentDueDate, previousBillDate;

              if (currentDay >= billingDay) {
                currentBillDate = new Date(currentYear, currentMonth, billingDay);
                previousBillDate = new Date(currentYear, currentMonth - 1, billingDay);
                if (dueDay >= billingDay) {
                  currentDueDate = new Date(currentYear, currentMonth, dueDay);
                } else {
                  currentDueDate = new Date(currentYear, currentMonth + 1, dueDay);
                }
              } else {
                currentBillDate = new Date(currentYear, currentMonth - 1, billingDay);
                previousBillDate = new Date(currentYear, currentMonth - 2, billingDay);
                if (dueDay >= billingDay) {
                  currentDueDate = new Date(currentYear, currentMonth - 1, dueDay);
                } else {
                  currentDueDate = new Date(currentYear, currentMonth, dueDay);
                }
              }

              // Calculate previous balance (all transactions before the previous bill date)
              let previousBalance = 0;
              const transactionsBeforePreviousBill = transactions.filter(tx => {
                const txDate = new Date(tx.date);
                return txDate < previousBillDate;
              });

              transactionsBeforePreviousBill.forEach(tx => {
                const amount = Math.abs(tx.amount);
                // Debits increase the outstanding balance
                if ((tx.type === 'expense' || tx.type === 'debit') && tx.amount < 0) {
                  previousBalance += amount;
                } else if (tx.type === 'transfer' && tx.amount < 0) {
                  previousBalance += amount;
                }
                // Credits reduce the outstanding balance
                else if ((tx.type === 'income' || tx.type === 'credit') && tx.amount > 0) {
                  previousBalance -= amount;
                } else if (tx.type === 'transfer' && tx.amount > 0) {
                  previousBalance -= amount;
                }
              });

              // Get billing cycle transactions (previous bill date to current bill date)
              const billingCycleTransactions = transactions.filter(tx => {
                const txDate = new Date(tx.date);
                return txDate >= previousBillDate && txDate < currentBillDate;
              });

              // Calculate current cycle bill amount
              const currentCycleBillAmount = billingCycleTransactions.reduce((sum, tx) => {
                const amount = Math.abs(tx.amount);
                if ((tx.type === 'expense' || tx.type === 'debit') && tx.amount < 0) {
                  return sum + amount;
                } else if (tx.type === 'transfer' && tx.amount < 0) {
                  return sum + amount;
                } else if ((tx.type === 'income' || tx.type === 'credit') && tx.amount > 0) {
                  return sum - amount;
                } else if (tx.type === 'transfer' && tx.amount > 0) {
                  return sum - amount;
                }
                return sum;
              }, 0);

              // Total bill amount = previous balance + current cycle amount
              const billAmount = previousBalance + currentCycleBillAmount;

              // Get payments made (from current bill date to today)
              const paymentTransactions = transactions.filter(tx => {
                const txDate = new Date(tx.date);
                return txDate >= currentBillDate &&
                  txDate <= today &&
                  ((tx.type === 'income' && tx.amount > 0) ||
                    (tx.type === 'credit') ||
                    (tx.type === 'payment') ||
                    (tx.type === 'transfer' && tx.amount > 0));
              });

              const paymentsMade = paymentTransactions.reduce((sum, tx) => {
                return sum + Math.abs(tx.amount);
              }, 0);

              // Determine payment status
              if (billAmount > 0) {
                creditCardStats.billGenerated++;

                const isFullyPaid = paymentsMade >= billAmount;
                if (isFullyPaid) {
                  creditCardStats.fullyPaid++;
                } else if (paymentsMade > 0) {
                  creditCardStats.partiallyPaid++;
                  const remainingPayment = Math.max(0, billAmount - paymentsMade);
                  creditCardStats.totalRemainingPayment += remainingPayment;
                } else {
                  creditCardStats.unpaid++;
                  creditCardStats.totalRemainingPayment += billAmount;
                }
              } else if (billAmount <= 0) {
                // Handle zero or negative bill amounts (no payment required)
                creditCardStats.billGenerated++;
                creditCardStats.fullyPaid++;
              }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching credit card statistics:', error);
      }

      // Calculate total net worth: Investments + Net Inhand - Loans
      const totalNetWorth = investmentTotal + netWorth - loanTotal;

      setAccountSummary({
        bankAccounts: bankTotal,
        creditCards: creditTotal,
        cashWallets: cashTotal,
        netInhand: netWorth,
        investments: investmentTotal,
        loans: loanTotal,
        totalNetWorth: totalNetWorth,
        creditCardStats: creditCardStats
      });

    } catch (error) {
      console.error('Error fetching account summary:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountSummary();
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Your finances at a glance
            </Typography>
          </Box>
          <TourButton onClick={dashboardTour.start} />
        </Box>

        {/* Quick Navigation Cards - Compact */}
        <Box sx={{ mb: 5 }} data-tour="dashboard-quick">
          <Typography variant="h5" sx={{
            mb: 3,
            fontWeight: 600,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 4,
              height: 24,
              background: 'linear-gradient(45deg, #1976d2, #1565c0)',
              borderRadius: 2
            }} />
            Quick Access
          </Typography>
          <Grid container spacing={1.5}>
            {dashboardItems.filter(item => isEnabled(moduleForPath(item.path)?.key)).map((item) => {
              const IconComponent = item.icon;
              return (
                <Grid item xs={4} sm={4} md={4} lg={12 / 9} key={item.title}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      height: '100%',
                      '&:hover': {
                        boxShadow: 'var(--fx-card-shadow-hover)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <CardContent sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                      p: 1.5,
                      '&:last-child': { pb: 1.5 }
                    }}>
                      <IconComponent
                        sx={{
                          fontSize: 36,
                          color: item.color,
                          mb: 1
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          lineHeight: 1.2,
                          textAlign: 'center'
                        }}
                      >
                        {item.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Financial Overview Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{
            mb: 3,
            fontWeight: 600,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 4,
              height: 24,
              background: 'linear-gradient(45deg, #1976d2, #1565c0)',
              borderRadius: 2
            }} />
            Financial Overview
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5.5}>
              <Card data-tour="dashboard-accounts" sx={{
                p: 2.5,
                height: '100%',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1rem'
                }}>
                  <Box sx={{
                    width: 3,
                    height: 16,
                    background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                    borderRadius: 1
                  }} />
                  Account Summary
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05))',
                        border: '1px solid rgba(33, 150, 243, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08))',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <BankIcon sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem' }}>
                          Bank Accounts
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5, fontSize: '0.95rem' }}>
                          {formatCurrency(accountSummary.bankAccounts)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.05))',
                        border: '1px solid rgba(244, 67, 54, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.08))',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <CreditIcon sx={{ fontSize: 24, color: 'error.main', mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem' }}>
                          Credit Used
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main', mt: 0.5, fontSize: '0.95rem' }}>
                          {formatCurrency(accountSummary.creditCards)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08))',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <CashIcon sx={{ fontSize: 24, color: 'success.main', mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem' }}>
                          Cash & Digital
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5, fontSize: '0.95rem' }}>
                          {formatCurrency(accountSummary.cashWallets)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        background: accountSummary.netInhand >= 0
                          ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05))'
                          : 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 152, 0, 0.05))',
                        border: accountSummary.netInhand >= 0
                          ? '1px solid rgba(33, 150, 243, 0.2)'
                          : '1px solid rgba(255, 152, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: accountSummary.netInhand >= 0
                            ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08))'
                            : 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 152, 0, 0.08))',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <NetInhandIcon sx={{
                          fontSize: 24,
                          color: accountSummary.netInhand >= 0 ? 'info.main' : 'warning.main',
                          mb: 0.5
                        }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem' }}>
                          Net Inhand
                        </Typography>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 700,
                          color: accountSummary.netInhand >= 0 ? 'info.main' : 'warning.main',
                          mt: 0.5,
                          fontSize: '0.95rem'
                        }}>
                          {formatCurrency(accountSummary.netInhand)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} md={2.17}>
              <Card data-tour="dashboard-investments" sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1rem'
                }}>
                  <Box sx={{
                    width: 3,
                    height: 16,
                    background: 'linear-gradient(45deg, #2196F3, #1976D2)',
                    borderRadius: 1
                  }} />
                  Investments
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05))',
                    border: '1px solid rgba(33, 150, 243, 0.2)',
                    transition: 'all 0.3s ease',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08))',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <TrendingUp sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem' }}>
                      Total Portfolio
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main', mt: 0.5 }}>
                      {formatCurrency(accountSummary.investments || 0)}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} md={2.17}>
              <Card data-tour="dashboard-loans" sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1rem'
                }}>
                  <Box sx={{
                    width: 3,
                    height: 16,
                    background: 'linear-gradient(45deg, #FF9800, #F57C00)',
                    borderRadius: 1
                  }} />
                  Loans
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 152, 0, 0.05))',
                    border: '1px solid rgba(255, 152, 0, 0.2)',
                    transition: 'all 0.3s ease',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 152, 0, 0.08))',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <LoansIcon sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem' }}>
                      Outstanding
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main', mt: 0.5 }}>
                      {formatCurrency(accountSummary.loans || 0)}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} md={2.16}>
              <Card data-tour="dashboard-networth" sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1rem'
                }}>
                  <Box sx={{
                    width: 3,
                    height: 16,
                    background: accountSummary.totalNetWorth >= 0
                      ? 'linear-gradient(45deg, #4CAF50, #388E3C)'
                      : 'linear-gradient(45deg, #F44336, #D32F2F)',
                    borderRadius: 1
                  }} />
                  Net Worth
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: accountSummary.totalNetWorth >= 0
                      ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))'
                      : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.05))',
                    border: accountSummary.totalNetWorth >= 0
                      ? '1px solid rgba(76, 175, 80, 0.2)'
                      : '1px solid rgba(244, 67, 54, 0.2)',
                    transition: 'all 0.3s ease',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      background: accountSummary.totalNetWorth >= 0
                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08))'
                        : 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.08))',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <TotalNetWorthIcon sx={{
                      fontSize: 36,
                      color: accountSummary.totalNetWorth >= 0 ? 'success.main' : 'error.main',
                      mb: 1
                    }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.3 }}>
                      Investments + Net Inhand - Loans
                    </Typography>
                    <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: accountSummary.totalNetWorth >= 0 ? 'success.main' : 'error.main',
                      mt: 0.5
                    }}>
                      {formatCurrency(accountSummary.totalNetWorth || 0)}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Credit Cards Section (hidden when the module is switched off) */}
        {isEnabled('credit-cards') && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{
            mb: 3,
            fontWeight: 600,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 4,
              height: 24,
              background: 'linear-gradient(45deg, #1976d2, #1565c0)',
              borderRadius: 2
            }} />
            Credit Cards
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={9}>
              <Card data-tour="dashboard-cards" onClick={() => navigate('/credit-cards')} sx={{
                cursor: 'pointer',
                p: { xs: 2, sm: 3 },
                minHeight: 160,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{
                    width: 3,
                    height: 18,
                    background: 'linear-gradient(45deg, #E91E63, #C2185B)',
                    borderRadius: 1
                  }} />
                  Card Statistics
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={0} sx={{ '& .MuiGrid-item': { px: 1.2 }, flex: 1, alignContent: 'center' }}>
                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('all', 'Total')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'text.primary', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Total
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {accountSummary.creditCardStats?.total || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('used', 'Used')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'info.main', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Used
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main' }}>
                          {accountSummary.creditCardStats?.used || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('bill_generated', 'Bill Generated')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'primary.main', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Bill Generated
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {accountSummary.creditCardStats?.billGenerated || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('paid_all', 'Fully Paid')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'success.main', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Fully Paid
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {accountSummary.creditCardStats?.fullyPaid || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('unpaid_all', 'Unpaid')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'error.main', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Unpaid
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                          {accountSummary.creditCardStats?.unpaid || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Box {...statLinkProps('partially_paid', 'Partially Paid')}>
                        <CreditIcon sx={{ fontSize: 20, color: 'warning.main', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Partially Paid
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                          {accountSummary.creditCardStats?.partiallyPaid || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card data-tour="dashboard-dues" onClick={() => navigate('/credit-cards?filter=outstanding')} sx={{
                cursor: 'pointer',
                p: { xs: 2, sm: 3 },
                minHeight: 160,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                boxShadow: 'var(--fx-card-shadow)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 'var(--fx-card-shadow-hover)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{
                    width: 3,
                    height: 18,
                    background: accountSummary.creditCardStats?.totalRemainingPayment > 0
                      ? 'linear-gradient(45deg, #F44336, #D32F2F)'
                      : 'linear-gradient(45deg, #4CAF50, #388E3C)',
                    borderRadius: 1
                  }} />
                  Outstanding Payments
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={0} sx={{ '& .MuiGrid-item': { px: 2.5 }, flex: 1, alignContent: 'center' }}>
                    <Grid item xs={12}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 2,
                        borderRadius: 2,
                        background: accountSummary.creditCardStats?.totalRemainingPayment > 0
                          ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.05))'
                          : 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))',
                        border: accountSummary.creditCardStats?.totalRemainingPayment > 0
                          ? '1px solid rgba(244, 67, 54, 0.2)'
                          : '1px solid rgba(76, 175, 80, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: accountSummary.creditCardStats?.totalRemainingPayment > 0
                            ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.08))'
                            : 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08))',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        {/* <MoneyIcon sx={{
                          fontSize: 32,
                          color: accountSummary.creditCardStats?.totalRemainingPayment > 0 ? 'error.main' : 'success.main',
                          mb: 1
                        }} /> */}
                        {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                          Total Remaining
                        </Typography> */}
                        <Typography variant="h6" sx={{
                          fontWeight: 500,
                          color: accountSummary.creditCardStats?.totalRemainingPayment > 0 ? 'error.main' : 'success.main',
                          mt: 0
                        }}>
                          {formatCurrency(accountSummary.creditCardStats?.totalRemainingPayment || 0)}
                        </Typography>
                        {/* {accountSummary.creditCardStats?.totalRemainingPayment > 0 && (
                          <Typography variant="caption" sx={{
                            display: 'block',
                            color: 'error.main',
                            mt: 1,
                            fontWeight: 500
                          }}>
                            Unpaid & Partially Paid
                          </Typography>
                        )} */}
                        {(!accountSummary.creditCardStats?.totalRemainingPayment || accountSummary.creditCardStats?.totalRemainingPayment === 0) && (
                          <Typography variant="caption" sx={{
                            display: 'block',
                            color: 'success.main',
                            mt: 1,
                            fontWeight: 500
                          }}>
                            All bills paid! 🎉
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>

        )}

        {/* Insurance overview (after Credit Cards) */}
        {isEnabled('insurance') && <InsuranceOverview />}

        {/* Recent Activity: last 5 across transactions, loan payments, insurance premiums, FASTag */}
        <RecentActivity />
      </Box>
      <GuidedTour steps={TOURS.dashboard} open={dashboardTour.open} onClose={dashboardTour.close} />
    </Box>
  );
};

export default Dashboard; 